import React, { useId } from 'react';
import { ColorInput } from 'src/modules/shared/components/atoms/inputs/color-input/color-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { isWebsiteHexColor } from '../../utils/website-brand-color.util';

/** Props for a secondary theme color field. */
interface WebsiteThemeColorPickerProps {
  /** Visible label, also used in the field's accessible names. */
  label: string;
  /** Current HEX value; empty keeps the design's own color. */
  value: string;
  /** Message shown while a typed value is not a valid HEX color. */
  invalidText: string;
  /** Called with the typed or picked color. */
  onChange: (value: string) => void;
  /** Whether the field is read-only for the current user. */
  disabled?: boolean;
}

/**
 * Optional secondary or accent color: the shared color field under its
 * label, with an error while a typed value is not a valid HEX color.
 *
 * @param props - Label, value, and change callback.
 * @returns The color field.
 */
export const WebsiteThemeColorPicker: React.FC<
  WebsiteThemeColorPickerProps
> = ({ label, value, invalidText, onChange, disabled = false }) => {
  const errorId = useId();
  const invalid = value.trim() !== '' && !isWebsiteHexColor(value);

  return (
    <div className="space-y-1.5">
      <Text size="small" weight="medium">
        {label}
      </Text>
      <ColorInput
        label={label}
        value={value}
        onChange={onChange}
        disabled={disabled}
        invalid={invalid}
        describedBy={invalid ? errorId : undefined}
      />
      {invalid ? (
        <div id={errorId}>
          <Text size="tiny" color="danger">
            {invalidText}
          </Text>
        </div>
      ) : null}
    </div>
  );
};
